import {useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import machineIcon from '../../../../../../assets/image/inputGroups/machinery.svg';
import Button from '../../../../../../components/button';
import DatePickerBottomSheet from '../../../../../../components/DatePickerBottomSheet';
import CustomCheckbox from '../../../../../../components/mazraeKit/customCheckbox';
import CustomHeader from '../../../../../../components/mazraeKit/headers/homeHeader';
import NoItemFoundPage from '../../../../../../components/noItemFoundPage';
import SpinnerLoading from '../../../../../../components/SpinnerLoading';
import {StoreOrderSteps} from '../../../../../../services/hookService/getOrderDetails/helper';
import useGetStoreOrderDetails from '../../../../../../services/hookService/getStoreOrderDetails/useGetOrderDetails';
import {
  DeliveryMethodValue,
  deliveryOptions,
} from '../../../../../../services/hookService/postBasket/deliveryOptions';
import usePostStoreOrderApproval from '../../../../../../services/hookService/postStoreOrderApproval/usePostStoreOrderApproval';
import usePostStoreOrderDelivered from '../../../../../../services/hookService/postStoreOrderDelivered/usePostStoreOrderDelivered';
import usePostStoreOrderDetails from '../../../../../../services/hookService/postStoreOrderDetails/usePostStoreOrderDetails';
import {
  getPersianDateTime,
  jalaliToGregorian,
} from '../../../../../../utilities/helper/GetPersianDateTime';
import MyStoreBuyerInformation from './components/MyStoreBuyerInformation';
import MyStoreOrderedItems from './components/MyStoreOrderedItems';
import MyStoreOrderInformation from './components/MyStoreOrderInformation';
import MyStoreOrderSupplyingStatus from './components/MyStoreOrderSupplyingStatus';
import StoreOrderQRCode from './components/QrCodeSection';
import QRScanner from '../../../../../../components/QrCode/QRScanner';
import {SelectAdsBottomSheet} from './components/SelectAdsBottomSheet';
import {toast} from 'react-toastify';
import {useUpdateQRCodes} from '../../../../../../services/qrcode/useQRCode';

// تاریخ تایید سفارش : approved_at
// تاریخ تامین سفارش : supply_date
// تاریخ ثبت : created_at

// this page was suppose to be a simple page hence the one page approach(and to think i wanted to mix order detail and store detail pages as one, the audacity). then all of the sudden design and backend needed to change stuff and then i have this mess with god knows how much conditions i have set. rip the future maintainer. don't curse me ty and consider making one page for each step.
const MyStoreOrderDetail = () => {
  const navigate = useNavigate();
  const {orderGuid} = useParams();
  const [showScanner, setShowScanner] = useState(false);
  const [selectBottomSheetOpen, setSelectBottomSheetOpen] = useState(false);
  const [selectedAdv, setSelectedAdv] = useState('');
  const [orderDelivered, setOrderDelivered] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [hasDeliveredToday, setHasDeliveredToday] = useState<boolean>(true);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [deliveryOption, setDeliveryOption] = useState<DeliveryMethodValue>(
    deliveryOptions[0].value,
  );
  const [trackingCode, setTrackingCode] = useState<string>('');
  const {
    mutate: updateQRCodes,
    isError: updateQrCodeHasError,
    error: updateQrCodeError,
  } = useUpdateQRCodes();
  const isFormValid = useMemo(() => {
    return Boolean(
      deliveryOption &&
        trackingCode.trim() &&
        uploadedImage &&
        (hasDeliveredToday || deliveryDate),
    );
  }, [
    deliveryOption,
    trackingCode,
    uploadedImage,
    hasDeliveredToday,
    deliveryDate,
  ]);

  const {data, isLoading} = useGetStoreOrderDetails({
    orderGuid,
    onSuccess: data => {
      const selectedOption = deliveryOptions.find(
        opt =>
          Number(opt.value) ===
          data?.store_order?.[0].delivery?.shipping_method,
      );
      if (selectedOption) setDeliveryOption(selectedOption?.value);

      if (data.store_order[0].step === StoreOrderSteps.STORE_DELIVERED)
        setOrderDelivered(true);
    },
  });
  const {mutate: postStoreOrderApproval, isLoading: isOrderApprovalLoading} =
    usePostStoreOrderApproval(() => navigate(-1));

  const {mutate: postStoreOrderDelivered, isLoading: isOrderDeliveredLoading} =
    usePostStoreOrderDelivered(() => navigate(-1));

  const {mutate: patchStoreOrderDetails, isLoading: isOrderDetailsLoading} =
    usePostStoreOrderDetails(() => navigate(-1));

  // Flatten all order items safely
  const allOrderItems = useMemo(() => {
    return (
      data?.store_order?.flatMap(
        storeOrder =>
          storeOrder?.order_items?.map(item => ({
            ...item,
            parentOrderGuid: data?.guid ?? '',
            parentCreatedAt: data?.created_at ?? '',
            storeOrderGuid: storeOrder?.guid ?? '',
            supplyDate: storeOrder?.supply_date ?? '',
            approvedAt: storeOrder?.approved_at ?? '',
            supplierProvideAt: storeOrder?.supplier_provide_at ?? '',
            storeOrderStep: storeOrder?.step ?? 0,
            store_detail: storeOrder?.store,
            delivery: storeOrder?.delivery,
          })) ?? [],
      ) ?? []
    );
  }, [data]);

  const firstOrderItem = allOrderItems[0];

  const isDeliveryInPerson = !Boolean(data?.profile_address);
  const isOrderProcessing =
    firstOrderItem?.storeOrderStep === StoreOrderSteps.PROCESSING;

  const isOrderPending =
    firstOrderItem?.storeOrderStep === StoreOrderSteps.PAYMENT_RECEIVED;

  const isOrderCompleted =
    firstOrderItem?.storeOrderStep === StoreOrderSteps.SHIPPED ||
    firstOrderItem?.storeOrderStep === StoreOrderSteps.USER_DELIVERED ||
    firstOrderItem?.storeOrderStep === StoreOrderSteps.STORE_DELIVERED ||
    firstOrderItem?.storeOrderStep === StoreOrderSteps.COMPLETED;

  const onQrSuccessHandler = (decodedText: string) => {
    const dataToSend = {id: selectedAdv, link: decodedText};
    updateQRCodes(dataToSend);
    setSelectBottomSheetOpen(false);
    setShowScanner(false);
  };
  const onScanFailedHandler = (errorMessage: string) => {
    toast.error(errorMessage);
    setSelectBottomSheetOpen(false);
    setShowScanner(false);
  };

  // Loading state
  if (
    isLoading ||
    isOrderApprovalLoading ||
    isOrderDeliveredLoading ||
    isOrderDetailsLoading
  ) {
    return (
      <div className="flex justify-center items-center h-dvh">
        <SpinnerLoading size={40} />
      </div>
    );
  }

  // No data found
  if (!data?.guid) {
    return (
      <div className="flex flex-col gap-3 justify-center items-center h-dvh">
        <NoItemFoundPage />
        <Button
          variant="primary"
          className="py-3"
          onClick={() => navigate('/')}
        >
          بازگشت به خانه
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-2 w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="flex relative gap-2 items-center"
            onClick={() => navigate(-1)}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">جزئیات سفارش</span>
          </div>
        )}
      />

      <div className="flex overflow-y-auto flex-col gap-3 p-3 w-full h-full">
        {/* Delivery code section */}
        {isDeliveryInPerson && isOrderProcessing && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-4 items-center px-4 py-2 w-full text-red-500 bg-red-50 rounded-lg text-md">
              <span>کد تحویل سفارش</span>
              <span>{firstOrderItem.delivery?.delivery_code}</span>
            </div>
            <div className="flex gap-2 items-start text-zinc-500">
              <i className="mt-0.5 text-lg icon-namereport" />
              <span className="text-md">
                کد تحویل سفارش را از کاربر گرفته و در صورت تطابق با این کد،
                محصولات را تحویل دهید.
              </span>
            </div>
          </div>
        )}

        {/* Ordered items */}
        {allOrderItems.length > 0 && (
          <>
            <div className="flex flex-col pb-2">
              <span className="py-2 text-sm font-medium text-zinc-500">
                موارد سفارش
              </span>
              {allOrderItems.map(item => (
                <MyStoreOrderedItems
                  isOrderPending={isOrderPending}
                  link={item?.link ?? undefined}
                  key={item?.guid ?? Math.random().toString()}
                  title={item?.title_snapshot ?? ''}
                  price={item?.price_snapshot ?? 0}
                  quantity={item?.quantity}
                  discount_price={item?.discount_price ?? 0}
                  // TODO: remove this hardcoded icon
                  image={item?.image ?? item?.sub_brand_image ?? machineIcon}
                  count={item?.quantity ?? 0}
                />
              ))}
            </div>

            {/* Order info */}
            <MyStoreOrderInformation
              createdDate={getPersianDateTime(
                firstOrderItem?.parentCreatedAt ?? '',
              )}
              supplyDate={getPersianDateTime(firstOrderItem?.supplyDate ?? '')}
              totalOrderPrice={data?.total_price}
              delivery={firstOrderItem?.delivery ?? null}
              address={data?.profile_address}
              step={data?.step ?? 0}
            />

            {/* Buyer info */}
            <MyStoreBuyerInformation
              profileAddress={data?.profile_address}
              buyerName={data?.user_profile.name}
              buyerState={data?.user_profile.state}
              buyerCity={data?.user_profile.city}
              buyerNumber={data?.user_profile.phone_number}
              isOrderCompleted={isOrderCompleted}
            />

            {/* Supplying status */}
            <MyStoreOrderSupplyingStatus
              uploadedImage={uploadedImage}
              deliveryOption={deliveryOption}
              trackingCode={trackingCode}
              setUploadedImage={setUploadedImage}
              setDeliveryOption={setDeliveryOption}
              setTrackingCode={setTrackingCode}
              isDeliveryInPerson={isDeliveryInPerson}
              orderStoreStep={firstOrderItem?.storeOrderStep ?? 0}
              deliveryDate={firstOrderItem?.delivery?.delivery_date ?? ''}
              receiptImage={firstOrderItem?.delivery?.shipping_receipt ?? ''}
              acceptDate={getPersianDateTime(firstOrderItem?.approvedAt ?? '')}
              supplierProvideAt={getPersianDateTime(
                firstOrderItem?.supplierProvideAt ?? '',
              )}
            />

            {/* Date picker for pending orders */}
            {firstOrderItem?.storeOrderStep ===
              StoreOrderSteps.PAYMENT_RECEIVED && (
              <DatePickerBottomSheet
                value={deliveryDate ?? ''}
                title="زمان تامین کالاها"
                placeholder="زمان تامین را مشخص کنید"
                onChange={setDeliveryDate}
                required
                disablePastDates
              />
            )}

            {/* Delivery confirmation checkbox */}
            {isOrderProcessing && isDeliveryInPerson && (
              <CustomCheckbox
                customClass="gap-1"
                name="hasOrderDelivered"
                title="سفارش را تحویل دادم"
                onChange={() => {
                  if (
                    firstOrderItem?.storeOrderStep ===
                    StoreOrderSteps.STORE_DELIVERED
                  )
                    return;
                  setOrderDelivered(prev => !prev);
                }}
                checked={orderDelivered}
              />
            )}

            {firstOrderItem?.storeOrderStep === StoreOrderSteps.PROCESSING &&
              !isDeliveryInPerson && (
                <CustomCheckbox
                  customClass="gap-1"
                  name="hasDeliveredToday"
                  title="سفارش را امروز ارسال کردم"
                  onChange={() => {
                    // emptying the date if toggling off
                    if (hasDeliveredToday) setDeliveryDate('');
                    setHasDeliveredToday(prev => !prev);
                  }}
                  checked={hasDeliveredToday}
                />
              )}

            {/*Date picker for selecting the date that order is getting delivered*/}
            {firstOrderItem?.storeOrderStep === StoreOrderSteps.PROCESSING &&
              !hasDeliveredToday && (
                <DatePickerBottomSheet
                  value={deliveryDate ?? ''}
                  title="تاریخ ارسال سفارش "
                  placeholder="تاریخ ارسال سفارش را مشخص کنید"
                  onChange={setDeliveryDate}
                  required
                  disablePastDates
                />
              )}

            {firstOrderItem?.storeOrderStep ===
              StoreOrderSteps.PAYMENT_RECEIVED && (
              <StoreOrderQRCode
                advertisementList={allOrderItems}
                setSelectBottomSheetOpen={setSelectBottomSheetOpen}
              />
            )}

            {/* Action buttons */}
            <div className="flex sticky -bottom-5 gap-3 justify-center pt-1 mt-auto w-full bg-white border-t">
              {isOrderPending && (
                <>
                  <Button
                    size="medium"
                    variant="errorOutline"
                    className="w-36"
                    onClick={() =>
                      postStoreOrderApproval({
                        action: 'reject',
                        supplier_provide_at: deliveryDate ?? undefined,
                        orderGuid: orderGuid ?? '',
                      })
                    }
                  >
                    رد کردن سفارش
                  </Button>
                  <Button
                    size="medium"
                    className="w-36 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={() =>
                      postStoreOrderApproval({
                        action: 'accept',
                        supplier_provide_at: deliveryDate ?? undefined,
                        orderGuid: orderGuid ?? '',
                      })
                    }
                    disabled={!deliveryDate}
                  >
                    تایید سفارش
                  </Button>
                </>
              )}

              {isOrderProcessing && !isDeliveryInPerson && (
                <Button
                  size="medium"
                  className="w-36 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={() =>
                    patchStoreOrderDetails({
                      order_guid: orderGuid as string,
                      shipping_method: deliveryOption,
                      shipment_number: trackingCode,
                      shipping_receipt: uploadedImage as File,
                      delivery_date: hasDeliveredToday
                        ? new Date().toISOString().slice(0, 10)
                        : jalaliToGregorian(deliveryDate),
                    })
                  }
                  disabled={!isFormValid}
                >
                  تامین و ارسال
                </Button>
              )}

              {isOrderProcessing && isDeliveryInPerson && (
                <>
                  <Button
                    size="medium"
                    variant="outline"
                    className="w-36"
                    onClick={() => navigate('/backup')}
                  >
                    پشتیبانی
                  </Button>
                  <Button
                    size="medium"
                    className="w-36 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={() =>
                      postStoreOrderDelivered({
                        orderGuid: orderGuid ?? '',
                      })
                    }
                    disabled={!orderDelivered}
                  >
                    ثبت
                  </Button>
                </>
              )}

              {isOrderCompleted && (
                <Button
                  size="medium"
                  className="w-36 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={() => navigate(-1)}
                >
                  {isDeliveryInPerson ? 'بستن' : 'بازگشت'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      {showScanner && (
        <QRScanner
          onClose={() => setShowScanner(false)}
          onScanFailure={onScanFailedHandler}
          onScanSuccess={onQrSuccessHandler}
        />
      )}
      {selectBottomSheetOpen && (
        <SelectAdsBottomSheet
          selectedAdvertisement={selectedAdv}
          isOpen={selectBottomSheetOpen}
          setSelectedAdvertisement={setSelectedAdv}
          setScannerOpen={setShowScanner}
          setIsOpen={setSelectBottomSheetOpen}
          advertisementList={allOrderItems}
        />
      )}
    </div>
  );
};

export default MyStoreOrderDetail;
